import React, { useEffect, useState, useContext } from 'react';
import classnames from 'classnames';

import Button from '../../../../components/UI/Button';
import Checkbox from '../../../../components/UI/Checkbox';
import CheckListItem from '../../../../components/UI/CheckListItem';
import Layout from '../../../Layout';
import { AnalyticsContext } from '../../../../ModularReduxAnalytics';

import css from './CoreScreen.module.scss';
import fccCss from '../FCC.module.scss';
import layoutCss from '../../../Layout/Layout.module.scss';
import { useToggles } from '../../../../state/toggles';
import { planPrices, savingsRewards } from '../../../../utils/prices';

interface CoreScreenProps {
  ageConfirmed: boolean;
  onAgeConfirmationToggle: () => void;
  onNextClick: () => void;
  onExplorePlansClick: () => void;
}

const CoreScreen = (props: CoreScreenProps) => {
  const [isAgeConfError, setIsAgeConfError] = useState(false);
  const { analytics } = useContext(AnalyticsContext);
  const toggles = useToggles();
  const plans = planPrices(toggles);
  const savings = savingsRewards(toggles);

  useEffect(() => {
    window.scrollTo(0, 0);
    analytics.page('Registration', 'FCC in Reg Set Plan');
  }, []);

  return (
    <Layout>
      <div className={css.layoutInner}>
        <h1>Sign up for Greenlight and apply for the Family Cash Card</h1>
        <p className={css.subheading}>
          Start your 1-month free<sup>1</sup> Greenlight Core trial ($
          {plans.standard}/month after) and apply for the Family Cash Card to
          earn up to 3% cash back* on purchases.
        </p>
        <div className={layoutCss.layoutWrapper}>
          <div className={css.container}>
            <picture>
              <source
                media="(max-width: 500px)"
                srcSet="../../../static/fcc-card.png"
              />
              <source
                media="(min-width: 501px)"
                srcSet="../../../static/fcc-card.png"
              />
              <img
                src="../../../static/fcc-card.png"
                alt="Greenlight Family Cash Card"
                className={css.banner}
                data-testid="banner"
              />
            </picture>
            <div className={css.infoWrapper}>
              <div className={css.infoContainer}>
                <h1 data-e2e="fcc-header" className={css.heading}>
                  Family Cash Card
                </h1>
                <div>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>
                      Credit card with up to 3% cash back<sup>*</sup> on
                      purchases. No limits, no categories.
                    </p>
                  </CheckListItem>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>
                      Redeem cash back your way, with the option to auto-invest.
                    </p>
                  </CheckListItem>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>Enjoy perks from World Mastercard Program.</p>
                  </CheckListItem>
                </div>
                <h1 data-e2e="core-plan-header" className={css.heading}>
                  Greenlight Core plan
                </h1>
                <div>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>Up to 5 debit cards for your kids.</p>
                  </CheckListItem>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>
                      Educational app to send money to your kids, set up chores,
                      set savings goals, and more.
                    </p>
                  </CheckListItem>
                  <CheckListItem redesignCheckListItem={true}>
                    <p>{savings.core} Savings Reward.**</p>
                  </CheckListItem>
                </div>
              </div>
            </div>
          </div>
          <Checkbox
            id="age-confirmation"
            checked={props.ageConfirmed}
            onChange={() => props.onAgeConfirmationToggle()}
            label={<div>I am 18 years of age or older</div>}
          />
          <div
            className={classnames({
              [css.disclaimer]: true,
              [css.disclaimerError]: isAgeConfError
            })}
          >
            Must be 18 years or older to apply for the Family Cash Card
            {isAgeConfError && (
              <img
                className={css.errorIcon}
                src="/static/error-icon.svg"
                alt="Error icon"
              />
            )}
          </div>
          <Button
            text="Next"
            loading={false}
            onClick={() => {
              if (!props.ageConfirmed) setIsAgeConfError(true);
              props.onNextClick();
            }}
            data-e2e="select-core"
          />
          <div
            className={fccCss.textButton}
            onClick={() => {
              if (!props.ageConfirmed) setIsAgeConfError(true);
              props.onExplorePlansClick();
            }}
          >
            Explore other Greenlight plans
          </div>
          <div className={fccCss.footer}>
            <p>
              <sup>*</sup>Earn 3% when you spend at least $4,000 in a billing
              cycle, 2% when you spend at least $1,000 but less than $4,000 in a
              billing cycle and 1% when you spend &lt;$1,000 in a billing cycle.
              See the Credit Card Rewards Terms and Conditions for details,
              including earning, redemption, expiration or forfeiture.
            </p>
            <p>Familial relationship not required to obtain benefits.</p>
            <p>
              The Greenlight Family Cash Card is issued by First National Bank
              of Omaha (FNBO®)pursuant to license by Mastercard International.
            </p>
            <p>
              <sup>**</sup>See Greenlight&apos;s Terms of Service for details on
              earnings limits and how to qualify. Subject to change at any time.
            </p>
            <p>
              <sup>1</sup>Subject to minimum balance requirements and identity
              verification. Plans start at ${plans.standard} after the first
              month. Offer subject to change at any time.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CoreScreen;
